import { z } from 'zod';
export const uuidSchema = z.uuid();
export const PLAN_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const PLAN_PUBLIC_PATH_MIN = 2;
export const PLAN_PUBLIC_PATH_MAX = 64;
export const RESERVED_PUBLIC_PATHS = new Set([
    'admin',
    'api',
    'app',
    'assets',
    'dashboard',
    'favicon',
    'files',
    'health',
    'healthz',
    'index',
    'login',
    'logout',
    'meta',
    'plan',
    'plans',
    'public',
    'robots',
    'site',
    'static',
    'uploads',
    'www',
    '__unlock',
]);
const PUBLIC_PATH_CHARS_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
export const planPublicPathSchema = z
    .string()
    .trim()
    .transform((value) => value.toLowerCase())
    .pipe(z
    .string()
    .min(PLAN_PUBLIC_PATH_MIN, `يجب أن يتكون المسار من ${PLAN_PUBLIC_PATH_MIN} أحرف على الأقل.`)
    .max(PLAN_PUBLIC_PATH_MAX, `يجب ألا يتجاوز المسار ${PLAN_PUBLIC_PATH_MAX} حرفًا.`)
    .regex(PUBLIC_PATH_CHARS_RE, 'استخدم أحرفًا إنجليزية صغيرة وأرقامًا وشرطات فقط، على أن يبدأ وينتهي بحرف أو رقم.')
    .refine((value) => !value.includes('--'), {
    message: 'لا تستخدم شرطتين متتاليتين.',
})
    .refine((value) => !value.startsWith('__') && !RESERVED_PUBLIC_PATHS.has(value), {
    message: 'هذا المسار محجوز.',
})
    .refine((value) => !PLAN_UUID_RE.test(value), {
    message: 'لا يمكن أن يكون المسار المخصص بصيغة UUID. اتركه فارغًا لاستخدام المعرف التلقائي.',
}));
/** Empty or null means “use the plan UUID”. Undefined means “leave unchanged” on PATCH. */
export const optionalPlanPublicPathSchema = z
    .union([z.string(), z.null()])
    .optional()
    .transform((value, ctx) => {
    if (value === undefined) {
        return undefined;
    }
    if (value === null || value.trim() === '') {
        return null;
    }
    const parsed = planPublicPathSchema.safeParse(value);
    if (!parsed.success) {
        ctx.addIssue({
            code: 'custom',
            message: parsed.error.issues[0]?.message ?? 'المسار العام غير صالح',
        });
        return z.NEVER;
    }
    return parsed.data;
});
export function isUuidPathSegment(segment) {
    return PLAN_UUID_RE.test(segment);
}
export function planPublicSegment(plan) {
    return plan.publicPath ?? plan.id;
}
export const planPublicSchema = z.object({
    id: uuidSchema,
    title: z.string().min(1).max(200),
    published: z.boolean(),
    passwordProtected: z.boolean(),
    publicPath: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    filesUpdatedAt: z.string().nullable(),
    publicUrl: z.string(),
});
export const createPlanBodySchema = z.object({
    title: z.string().trim().min(1, 'العنوان مطلوب.').max(200, 'العنوان طويل جدًا.'),
    published: z.boolean().optional().default(false),
    publicPath: optionalPlanPublicPathSchema,
});
export const patchPlanBodySchema = z
    .object({
    title: z.string().trim().min(1, 'العنوان مطلوب.').max(200, 'العنوان طويل جدًا.').optional(),
    published: z.boolean().optional(),
    publicPath: optionalPlanPublicPathSchema,
})
    .refine((value) => value.title !== undefined || value.published !== undefined || value.publicPath !== undefined, {
    message: 'يلزم عنوان أو حالة نشر أو مسار عام',
});
export const setPlanPasswordBodySchema = z.object({
    password: z
        .string()
        .min(10, 'يجب أن تتكون كلمة المرور من 10 أحرف على الأقل.')
        .max(200, 'كلمة المرور طويلة جدًا.'),
});
export const loginBodySchema = z.object({
    username: z.string().min(1).max(128),
    password: z.string().min(1).max(1024),
});
export const sessionResponseSchema = z.object({
    authenticated: z.boolean(),
    username: z.string().optional(),
    csrfToken: z.string().optional(),
});
export const planResponseSchema = z.object({
    plan: planPublicSchema,
});
export const plansResponseSchema = z.object({
    plans: z.array(planPublicSchema),
});
export const unlockBodySchema = z.object({
    password: z.string().min(1).max(1024),
});
//# sourceMappingURL=plan.js.map